/**
 * Voice Recording Service for Luna Language Learning
 * Handles browser audio recording, Whisper transcription, and audio processing
 * Designed for children aged 8-14 with simple, intuitive interface
 */

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000'

class VoiceRecordingService {
    constructor() {
        this.mediaRecorder = null;
        this.audioStream = null;
        this.audioChunks = [];
        this.isRecording = false;
        this.isSupported = this.checkBrowserSupport();
        
        // Recording configuration
        this.config = {
            mimeType: this.getBestMimeType(),
            audioBitsPerSecond: 128000, // Good quality for speech
            maxDuration: 60000, // 60 seconds max
            silenceThreshold: 0.01, // For silence detection
        };
        
        // Event callbacks
        this.onStart = null;
        this.onStop = null;
        this.onError = null;
        this.onDataAvailable = null;
        this.onVolumeChange = null;
        
        // Volume monitoring
        this.audioContext = null;
        this.analyser = null;
        this.volumeMonitorInterval = null;
    }
    
    /**
     * Check if browser supports audio recording
     */
    checkBrowserSupport() {
        return !!(navigator.mediaDevices && 
                  navigator.mediaDevices.getUserMedia && 
                  window.MediaRecorder);
    }
    
    /**
     * Get the best available MIME type for recording
     */
    getBestMimeType() {
        const types = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
            'audio/wav'
        ];
        
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }
        return 'audio/webm'; // Fallback
    }
    
    /**
     * Initialize audio stream and MediaRecorder
     */
    async initialize() {
        if (!this.isSupported) {
            throw new Error('Audio recording is not supported in this browser');
        }
        
        try {
            // Request microphone access
            this.audioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 44100
                }
            });
            
            // Initialize MediaRecorder
            this.mediaRecorder = new MediaRecorder(this.audioStream, {
                mimeType: this.config.mimeType,
                audioBitsPerSecond: this.config.audioBitsPerSecond
            });
            
            // Set up event handlers
            this.setupEventHandlers();
            
            // Initialize audio context for volume monitoring
            this.initializeVolumeMonitoring();
            
            return true;
        } catch (error) {
            console.error('Failed to initialize audio recording:', error);
            throw new Error('Microphone access denied or not available');
        }
    }
    
    /**
     * Set up MediaRecorder event handlers
     */
    setupEventHandlers() {
        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.audioChunks.push(event.data);
                if (this.onDataAvailable) {
                    this.onDataAvailable(event.data);
                }
            }
        };
        
        this.mediaRecorder.onstart = () => {
            this.isRecording = true;
            this.audioChunks = [];
            if (this.onStart) {
                this.onStart();
            }
        };
        
        this.mediaRecorder.onstop = () => {
            this.isRecording = false;
            this.stopVolumeMonitoring();
            if (this.onStop) {
                this.onStop();
            }
        };
        
        this.mediaRecorder.onerror = (event) => {
            console.error('MediaRecorder error:', event.error);
            if (this.onError) {
                this.onError(event.error);
            }
        };
    }
    
    /**
     * Initialize volume monitoring for visual feedback
     */
    initializeVolumeMonitoring() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            const source = this.audioContext.createMediaStreamSource(this.audioStream);
            source.connect(this.analyser);
            
            this.analyser.fftSize = 256;
            this.analyser.smoothingTimeConstant = 0.8;
        } catch (error) {
            console.warn('Volume monitoring not available:', error);
        }
    }
    
    /**
     * Start recording audio
     */
    async startRecording() {
        if (!this.mediaRecorder) {
            await this.initialize();
        }
        
        if (this.mediaRecorder.state === 'inactive') {
            this.mediaRecorder.start(100); // Collect data every 100ms
            this.startVolumeMonitoring();
            
            // Auto-stop after max duration
            setTimeout(() => {
                if (this.isRecording) {
                    this.stopRecording();
                }
            }, this.config.maxDuration);
            
            return true;
        }
        return false;
    }
    
    /**
     * Stop recording audio
     */
    stopRecording() {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            this.mediaRecorder.stop();
            return true;
        }
        return false;
    }
    
    /**
     * Start volume monitoring for visual feedback
     */
    startVolumeMonitoring() {
        if (!this.analyser) return;
        
        const bufferLength = this.analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        this.volumeMonitorInterval = setInterval(() => {
            this.analyser.getByteFrequencyData(dataArray);
            
            // Calculate volume level (0-100)
            const average = dataArray.reduce((a, b) => a + b) / bufferLength;
            const volumeLevel = Math.round((average / 255) * 100);
            
            if (this.onVolumeChange) {
                this.onVolumeChange(volumeLevel);
            }
        }, 100);
    }
    
    /**
     * Stop volume monitoring
     */
    stopVolumeMonitoring() {
        if (this.volumeMonitorInterval) {
            clearInterval(this.volumeMonitorInterval);
            this.volumeMonitorInterval = null;
        }
    }
    
    /**
     * Get recorded audio as Blob
     */
    getAudioBlob() {
        if (this.audioChunks.length === 0) {
            return null;
        }
        
        return new Blob(this.audioChunks, { 
            type: this.config.mimeType 
        });
    }
    
    /**
     * Get recorded audio as File for upload
     */
    getAudioFile(filename = null) {
        const blob = this.getAudioBlob();
        if (!blob) return null;
        
        const timestamp = new Date().getTime();
        const defaultName = `recording_${timestamp}.webm`;
        
        return new File([blob], filename || defaultName, {
            type: this.config.mimeType,
            lastModified: Date.now()
        });
    }
    
    /**
     * Upload audio to Whisper transcription endpoint
     */
    async transcribeAudio(language = 'en', learningMode = true) {
        const audioFile = this.getAudioFile();
        if (!audioFile) {
            return {
                success: false,
                error: 'No audio recorded',
                fallback_message: 'No audio was recorded. Please try again! 🎤'
            };
        }
        
        const formData = new FormData();
        formData.append('audio', audioFile);
        formData.append('language', language);
        formData.append('learning_mode', learningMode.toString());
        
        try {
            
            
            const response = await fetch(`${API_BASE_URL}/api/language/speech/transcribe`, {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            
            // Always return the result, even if it's an error
            // This allows the calling code to handle the response appropriately
            return {
                success: response.ok,
                ...result
            };
            
        } catch (error) {
            console.error('Transcription error:', error);
            return {
                success: false,
                error: error.message,
                fallback_message: 'Voice processing failed. Please try again! 🎤'
            };
        }
    }
    
    /**
     * Record and transcribe in one operation
     */
    async recordAndTranscribe(options = {}) {
        const {
            language = 'en',
            learningMode = true,
            maxDuration = 30000, // 30 seconds default
            onStart = null,
            onStop = null,
            onVolumeChange = null
        } = options;
        
        return new Promise((resolve, reject) => {
            // Set up callbacks
            this.onStart = onStart;
            this.onVolumeChange = onVolumeChange;
            
            // Custom stop handler that transcribes
            this.onStop = async () => {
                if (onStop) onStop();
                
                try {
                    const result = await this.transcribeAudio(language, learningMode);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            };
            
            // Start recording (handle async operation)
            this.startRecording()
                .then(() => {
                    // Auto-stop after specified duration
                    setTimeout(() => {
                        if (this.isRecording) {
                            this.stopRecording();
                        }
                    }, maxDuration);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
    
    /**
     * Clean up resources
     */
    cleanup() {
        this.stopRecording();
        this.stopVolumeMonitoring();
        
        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
        }
        
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        
        this.mediaRecorder = null;
        this.audioChunks = [];
    }
    
    /**
     * Get recording status and info
     */
    getStatus() {
        return {
            isSupported: this.isSupported,
            isRecording: this.isRecording,
            hasAudio: this.audioChunks.length > 0,
            mimeType: this.config.mimeType,
            duration: this.isRecording ? Date.now() - this.startTime : 0
        };
    }
}

// Export singleton instance
export default new VoiceRecordingService(); 